import { AuthError } from "./errors";
import type { TokenSet, TokenStore } from "./token-store";

/** A constant name only: token material must never enter lock names or messages. */
const LOCK_NAME = "tax-operations-session-refresh";

export async function refreshSharedSession(
  store: TokenStore,
  expectedRefresh: string,
  refresh: (token: string, source: "platform" | "passkey") => Promise<TokenSet>,
  locks: Pick<LockManager, "request"> | undefined = globalThis.navigator?.locks,
): Promise<TokenSet> {
  const operation = async (): Promise<TokenSet> => {
    store.sync();
    const before = store.snapshot();
    const refreshToken = store.getRefreshToken();
    if (!refreshToken) throw new AuthError("exchange", "Session ended", null, "401");
    if (before && refreshToken !== expectedRefresh && store.getIdToken()) return before;
    try {
      const next = await refresh(refreshToken, store.getSessionSource());
      store.sync();
      const current = store.snapshot();
      // A sign-out or new sign-in while the request ran owns the session now.
      if (store.getRefreshToken() !== refreshToken) {
        if (current) return current;
        throw new AuthError("exchange", "Session ended", null, "401");
      }
      store.set(next);
      return next;
    } catch (error) {
      store.sync();
      const current = store.snapshot();
      if (current && current.refreshToken !== refreshToken) return current;
      throw error;
    }
  };
  // Without Web Locks, retain same-tab single-flight and adopt newer stored
  // sessions, but concurrent cross-tab rotations cannot be fully prevented.
  return locks ? locks.request(LOCK_NAME, operation) : operation();
}
